import { Counter, Tooltip, Flex, Button } from '@/_components';
import type { FC } from 'react';
import type { DocumentsCellProps } from './DocumentsCell.types';
import { isEmpty, truncateString } from '@/_utils/strings';
import { removeHtml } from '@/_utils/sanitize';
import { DocumentsTooltipContent } from './components/DocumentsTooltipContent';
import {
  PREDEFINED_PROVIDED_ELSEWHERE,
  PREDEFINED_NOT_APPLICABLE,
  PREDEFINED_MARKED_COMPLETED,
} from '@/_constants/questions';

export const DocumentsCell: FC<DocumentsCellProps> = ({
  allCount,
  isLocked,
  doneCount,
  documentsNeeded,
  documentsUploaded,
  hasSplitDocumentsToReview,
  onOpenReview,
}) => {
  const getDocumentsNeeded = (
    documents: Array<[Array<string>, string, boolean]>,
  ): Array<string> => {
    const requiredDocumentNames: Array<string> = documents?.map((document) => {
      const isNameEmpty: boolean = isEmpty(document[0]);
      const documentName: string = truncateString(
        removeHtml(isNameEmpty ? document[1] : document[0].join('/')),
        32,
      );
      const isOptional: boolean = Boolean(document && document[2]);
      const optionalString: string = isOptional ? ' (Optional)' : '';
      const cleanString: string = documentName
        .replace('Please upload ', '')
        .replace('Please provide ', '')
        .replace('Upload ', '')
        .trim();

      // Ensure cleanString is not empty
      if (cleanString === '') {
        return isOptional ? '(empty) (Optional)' : '(empty)';
      }

      return cleanString + optionalString;
    });

    return requiredDocumentNames;
  };

  const getDocumentsByStatus = (status: string): Array<string> | undefined => {
    return documentsUploaded
      ?.filter((document: any) => document.answer === status)
      .map((d: any) => d.options?.[0]);
  };

  const elsewhereDocumentNames: string[] | undefined = getDocumentsByStatus(
    PREDEFINED_PROVIDED_ELSEWHERE,
  );
  const notApplicableDocumentNames: string[] | undefined = getDocumentsByStatus(
    PREDEFINED_NOT_APPLICABLE,
  );
  const markedCompletedDocumentNames: string[] | undefined =
    getDocumentsByStatus(PREDEFINED_MARKED_COMPLETED);

  const neededDocumentNames: Array<string> =
    getDocumentsNeeded(documentsNeeded);

  return (
    <Flex isYCentered={true} gap={2}>
      <Tooltip>
        <Tooltip.Content>
          <DocumentsTooltipContent
            allCount={allCount}
            doneCount={doneCount}
            documentsNeeded={neededDocumentNames}
            documentsProvidedElsewhere={elsewhereDocumentNames}
            documentsNotApplicable={notApplicableDocumentNames}
            documentsMarkedCompleted={markedCompletedDocumentNames}
            hasSplitDocumentsToReview={hasSplitDocumentsToReview}
          />
        </Tooltip.Content>
        <Tooltip.Trigger>
          <Counter
            {...{ allCount, doneCount, optionalCount: 0 }}
            isDisabled={isLocked}
          />
        </Tooltip.Trigger>
      </Tooltip>

      {hasSplitDocumentsToReview && (
        <Button
          intent="secondary"
          size="tiny"
          value="Review Split"
          onClick={(e) => {
            e.stopPropagation();
            onOpenReview?.();
          }}
          withTooltip
          tooltip="Review split documents"
          isIconOnly={true}
          iconLeft="file-magnifying-glass"
          tooltipPlacement="bottom"
        />
      )}
    </Flex>
  );
};
